import React, { useMemo } from 'react';
import useStyles from './Chart.styles';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Box, Typography } from '@material-ui/core';

import { useTranslation } from 'react-i18next';
import { themeColors } from '../../../../styles/themeColors';
import tPaths from '../../../../utils/tPaths';
import { config } from '../../../../constants/config';

interface IChartProps {
  data: {
    name: number | string;
    previous: number;
    selected: number;
  }[];
  title: string;
}

const Chart: React.FC<IChartProps> = ({ data, title }) => {
  const styles = useStyles();

  const { t } = useTranslation();

  const selectedAverage = useMemo(
    () => (data.reduce((acc, cv) => acc + cv.selected, 0) / data.length).toFixed(),
    [data],
  );
  const prevAverage = useMemo(
    () => (data.reduce((acc, cv) => acc + cv.previous, 0) / data.length).toFixed(),
    [data],
  );

  return (
    <Box className={styles.tableContainer}>
      <Box marginLeft={4}>
        <Typography variant="h5">{title}</Typography>
      </Box>

      <TextForAverage selected={selectedAverage} previous={prevAverage} range={data.length} />
      <LineChart {...config.lineChartDimensions} data={data} className={styles.table}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Line
          dataKey={'selected'}
          name={t(tPaths.dashboard.selected)}
          stroke={themeColors.selected}
          strokeWidth={2}
        />
        <Line
          dataKey={'previous'}
          name={t(tPaths.dashboard.previous)}
          stroke={themeColors.inactive}
          strokeWidth={2}
        />
      </LineChart>
    </Box>
  );
};

export default Chart;

interface IProps {
  previous: number | string;
  selected: number | string;
  range: number | string;
}

const TextForAverage: React.FC<IProps> = ({ selected, previous, range }) => {
  const { t } = useTranslation();

  return (
    <Box display="flex" marginLeft={2}>
      <Box margin={2}>
        <Typography variant="subtitle2">
          {t(tPaths.dashboard.averageSelectedDays, { days: range })}
        </Typography>
        <Typography variant="h6" color="secondary">
          {selected}
        </Typography>
      </Box>
      <Box margin={2}>
        <Typography variant="subtitle2">
          {t(tPaths.dashboard.averagePreviousDays, { days: range })}
        </Typography>
        <Typography variant="h6" style={{ color: themeColors.inactive }}>
          {previous}
        </Typography>
      </Box>
    </Box>
  );
};
